import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { loadSettings } from '@/config/settings';
import { drawArcadeBackground } from '@/utils/arcadeBackground';
import { VFXManager } from '@/objects/VFXManager';
import type { ScoredQuality } from '@/systems/ScoringSystem';
import { AudioManager } from '@/systems/AudioManager';
import { getInputThresholdMs, type DifficultyConfig } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';
import { calculateHorizontalLayouts, type LaneLayout } from '@/game/LaneLayoutManager';
import { PlayerLane, type LaneHitEvent, type LanePenaltyEvent } from '@/game/PlayerLane';
import { InputManager } from '@/systems/InputManager';
import { mountAccountBadge } from '@/ui/accountBadge';
import { setDiscordDailyPresence } from '@/discord';
import { getLocalSeatColor } from '@/utils/seatColors';
import { fetchDailyChallenge } from '@/services/dailyChallenge';
import type { DailyRampConfig } from '@/types/dailyChallenge';
import { createButton } from '@/ui/buttons';
import { formatDailyDisplayDate } from '@/utils/dailyDate';
import { trackGameStart, trackGameComplete } from '@/services/analytics';

interface DailyChallengeSceneData {
  requestedDate?: string | null;
}

export class DailyChallengeScene extends Phaser.Scene {
  private requestedDate: string | null = null;
  private dailyDate: string = '';
  private dailyId: string = '';
  private message: string = '';

  private eliminated: boolean = false;

  private difficultyConfig: DifficultyConfig = { name: 'Daily', scrollSpeed: 150 };
  private rampConfig: DailyRampConfig | null = null;
  private currentSpeed: number = 150;
  private rampStartTime: number = 0;
  private rampTickMs: number = 20_000;
  private rampStep: number = 20;
  private nextRampAt: number = 0;

  private lane!: PlayerLane;
  private layout!: LaneLayout;
  private extraInputManagers: InputManager[] = [];

  private vfxManager!: VFXManager;
  private audioManager!: AudioManager;

  private isPlaying: boolean = false;
  private gameStartTime: number = 0;
  private notesFinished: boolean = false;
  private notesFinishedTime: number = 0;
  private inputDisabled: boolean = false;
  private inputDisabledTime: number = 0;
  private readonly INPUT_GRACE_PERIOD = 2000;
  private activeToneHolders: number = 0;
  private dateText: Phaser.GameObjects.Text | null = null;
  private loadingText: Phaser.GameObjects.Text | null = null;
  private speedPulse: Phaser.Tweens.Tween | null = null;
  private speedUpText: Phaser.GameObjects.Text | null = null;
  private targetChunks: string[] = [];
  private nextChunkIndex: number = 0;
  private wordLengths: number[] = [];
  private currentWordIndex: number = 0;
  private currentWordBuffer: string = '';
  private pendingWords: string[] = [];
  private transcriptLines: string[] = [];
  private transcriptTexts: Phaser.GameObjects.Text[] = [];
  private transcriptMeasure: Phaser.GameObjects.Text | null = null;
  private transcriptArea: { x: number; y: number; width: number; lineHeight: number } | null = null;
  private transcriptMaxLines: number = 4;
  private meterValue: number = 70;
  private meterBackground: Phaser.GameObjects.Graphics | null = null;
  private meterFill: Phaser.GameObjects.Graphics | null = null;
  private meterLabel: Phaser.GameObjects.Text | null = null;
  private meterGaugeWidth: number = 0;
  private meterGaugeX: number = 0;
  private meterGaugeY: number = 0;
  private meterGaugeHeight: number = 0;
  private meterTargetWidth: number = 0;
  private meterDisplayWidth: number = 0;
  private meterFillColor: number = THEME.colors.hit.goodHex;
  private consecutiveMisses: number = 0;
  private readonly morseMeterBorderColor = THEME.colors.primaryHex;

  // These labels should read as global “playfield headers”, not lane-local annotations.
  private readonly LABEL_Y = 160;

  constructor() {
    super({ key: 'DailyChallengeScene' });
  }

  init(data: DailyChallengeSceneData): void {
    this.requestedDate = data?.requestedDate ?? null;
    this.dailyDate = '';
    this.dailyId = '';
    this.message = '';
    this.eliminated = false;
    this.meterValue = 70;
    this.meterTargetWidth = 0;
    this.meterDisplayWidth = 0;
    this.consecutiveMisses = 0;
    this.difficultyConfig = { name: 'Daily', scrollSpeed: 150 };
    this.rampConfig = null;
    this.currentSpeed = 150;
    this.rampStartTime = 0;
    this.rampTickMs = 15_000;
    this.rampStep = 25;
    this.nextRampAt = 0;

    this.isPlaying = false;
    this.gameStartTime = 0;
    this.notesFinished = false;
    this.notesFinishedTime = 0;
    this.inputDisabled = false;
    this.inputDisabledTime = 0;
    this.activeToneHolders = 0;
    this.extraInputManagers = [];
    this.speedPulse = null;
    this.speedUpText = null;
    this.targetChunks = [];
    this.nextChunkIndex = 0;
    this.wordLengths = [];
    this.currentWordIndex = 0;
    this.currentWordBuffer = '';
    this.pendingWords = [];
    this.transcriptLines = [];
    this.transcriptTexts = [];
    this.transcriptMeasure = null;
    this.transcriptArea = null;
  }

  create(): void {
    this.cameras.main.fadeIn(300, 5, 7, 22);
    drawArcadeBackground(this);
    mountAccountBadge(this);

    // Track daily challenge start
    trackGameStart({ mode: 'daily' });

    this.loadingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Loading daily challenge…', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    void this.loadDailyChallenge();
  }

  update(time: number, delta: number): void {
    if (!this.isPlaying) return;

    this.applyRamp(time);
    this.animateMeterFill();

    this.lane.update(time, delta);
    this.checkPassedNotes();

    if (this.lane.noteTrack.isComplete() && !this.notesFinished) {
      this.notesFinished = true;
      this.notesFinishedTime = time;
    }

    if (this.notesFinished && !this.inputDisabled) {
      const timeSinceNotesFinished = time - this.notesFinishedTime;
      if (timeSinceNotesFinished > this.INPUT_GRACE_PERIOD) {
        this.inputDisabled = true;
        this.inputDisabledTime = time;
        this.lane.stopInput();
        for (const manager of this.extraInputManagers) {
          manager.disable();
        }
        this.activeToneHolders = 0;
        this.audioManager.stopTone();
      }
    }

    if (this.inputDisabled) {
      const timeSinceDisabled = time - this.inputDisabledTime;
      if (timeSinceDisabled > 250 && this.lane.sentTrack.isEmpty()) {
        this.endGame();
      }
    }

    if (!this.eliminated) {
      this.ensureTargetBuffer();
      this.processPendingWords();
    }
  }

  shutdown(): void {
    this.lane?.noteTrack.destroy();
    this.lane?.destroy();
    this.vfxManager?.destroy();
    this.audioManager?.destroy();
    for (const manager of this.extraInputManagers) {
      manager.destroy();
    }
    this.extraInputManagers = [];
    for (const line of this.transcriptTexts) {
      line.destroy();
    }
    this.transcriptTexts = [];
    this.transcriptMeasure?.destroy();
    this.transcriptMeasure = null;
    this.meterBackground?.destroy();
    this.meterBackground = null;
    this.meterFill?.destroy();
    this.meterFill = null;
    this.meterLabel?.destroy();
    this.meterLabel = null;
    }

  private async loadDailyChallenge(): Promise<void> {
    const result = await fetchDailyChallenge(this.requestedDate ?? undefined);
    if (!result.ok) {
      this.loadingText?.setText('Daily challenge unavailable.');
      if (result.error === 'challenge_expired') {
        this.showExpiredState(result.currentDate ?? null);
      } else {
        this.showErrorState();
      }
      return;
    }

    const data = result.data;
    this.dailyDate = data.date;
    this.dailyId = data.id;
    this.message = data.message;
    this.rampConfig = data.ramp;
    this.currentSpeed = data.ramp.startSpeed;
    this.difficultyConfig = { name: 'Daily', scrollSpeed: data.ramp.startSpeed };
    this.rampTickMs = data.ramp.tickMs;
    this.rampStep = data.ramp.step;

    this.loadingText?.destroy();
    this.loadingText = null;

    this.setupHud();

    this.layout = calculateHorizontalLayouts(1)[0];
    this.setupMeterHud();
    this.vfxManager = new VFXManager(this);
    this.audioManager = AudioManager.get(this);
    this.audioManager.playSfx('start_game');
    this.createLane();
    this.setupTranscriptArea();
    this.lane.showCountdown().then(() => this.startGame());

    setDiscordDailyPresence();

    this.input.keyboard?.on('keydown-ESC', () => {
      this.endGame();
    });
  }

  private showExpiredState(currentDate: string | null): void {
    const body = currentDate
      ? `That link is expired. Today is ${currentDate}.`
      : 'That link is expired.';

    const message = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 40, body, {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      align: 'center',
    }).setOrigin(0.5);

    createButton(this, {
      x: GAME_WIDTH / 2 - 180,
      y: GAME_HEIGHT / 2 + 120,
      text: 'PLAY TODAY',
      style: 'primary',
      buttonType: 'normal',
      onClick: () => this.startDailyChallengeScene(),
    });

    createButton(this, {
      x: GAME_WIDTH / 2 + 180,
      y: GAME_HEIGHT / 2 + 120,
      text: 'MAIN MENU',
      style: 'secondary',
      buttonType: 'normal',
      onClick: () => this.scene.start('MenuScene'),
    });

    message.setDepth(5);
  }

  private showErrorState(): void {
    createButton(this, {
      x: GAME_WIDTH / 2 - 180,
      y: GAME_HEIGHT / 2 + 120,
      text: 'RETRY',
      style: 'primary',
      buttonType: 'normal',
      onClick: () => this.startDailyChallengeScene({ requestedDate: this.requestedDate }),
    });

    createButton(this, {
      x: GAME_WIDTH / 2 + 180,
      y: GAME_HEIGHT / 2 + 120,
      text: 'MAIN MENU',
      style: 'secondary',
      buttonType: 'normal',
      onClick: () => this.scene.start('MenuScene'),
    });
  }

  private startDailyChallengeScene(data?: { requestedDate: string | null }): void {
    this.scene.start('DailyChallengeScene', data);
  }

  private setupHud(): void {
    const hudBar = this.add.rectangle(GAME_WIDTH / 2, 60, GAME_WIDTH, 120, THEME.colors.backgroundLightHex, 0.68);
    hudBar.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    hudBar.setDepth(-1);

    const footer = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 40, GAME_WIDTH, 80, THEME.colors.backgroundLightHex, 0.68);
    footer.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    footer.setDepth(-1);

    this.add.text(GAME_WIDTH / 2, 14, 'DAILY CHALLENGE', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5, 0);

    this.dateText = this.add.text(GAME_WIDTH / 2, 42, formatDailyDisplayDate(this.dailyDate), {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5, 0);

    this.add.text(40, this.LABEL_Y, 'TRANSMITTED:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    });

    this.add.text(GAME_WIDTH - 40, this.LABEL_Y, 'TARGET:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(1, 0);

    const settings = loadSettings();
    const keyLabel = settings.inputBindings[0]?.type === 'keyboard'
      ? (settings.inputBindings[0].key === 'SPACE' ? 'SPACE' : settings.inputBindings[0].key)
      : settings.inputKey;

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 52,
      `${keyLabel} or LMB — TAP for dot, HOLD for dash`, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.text,
      }).setOrigin(0.5);

    this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, 'ESC = quit', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
  }

  private setupMeterHud(): void {
    if (!this.layout) return;
    const gaugeWidth = Math.min(280, this.layout.trackWidth * 0.48);
    const gaugeHeight = 18;
    const gaugeX = this.layout.trackX + this.layout.trackWidth - gaugeWidth;
    const gaugeY = this.layout.trackY + this.layout.trackHeight / 2 + 125;

    this.meterGaugeWidth = gaugeWidth;
    this.meterGaugeX = gaugeX;
    this.meterGaugeY = gaugeY;
    this.meterGaugeHeight = gaugeHeight;

    this.meterBackground = this.add.graphics();
    this.meterFill = this.add.graphics();
    this.meterBackground.setDepth(1);
    this.meterFill.setDepth(2);

    this.drawMeterBackground();

    this.meterLabel = this.add.text(gaugeX + gaugeWidth / 2, gaugeY - gaugeHeight / 2 - 10, 'MORSE METER', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.small,
      color: THEME.colors.textMuted,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5, 1);

    this.updateMeterTarget();
    this.meterDisplayWidth = this.meterTargetWidth;
    this.redrawMeterFill();
  }

  private updateSpeedHud(): void {
    return;
  }

  private drawMeterBackground(): void {
    if (!this.meterBackground) return;
    const radius = Math.min(this.meterGaugeHeight, 16);
    const x = this.meterGaugeX;
    const y = this.meterGaugeY - this.meterGaugeHeight / 2;
    this.meterBackground.clear();
    this.meterBackground.fillStyle(THEME.colors.backgroundPanelHex, 0.9);
    this.meterBackground.fillRoundedRect(x, y, this.meterGaugeWidth, this.meterGaugeHeight, radius);
    this.meterBackground.lineStyle(3, this.morseMeterBorderColor, 1);
    this.meterBackground.strokeRoundedRect(x, y, this.meterGaugeWidth, this.meterGaugeHeight, radius);
  }

  private redrawMeterFill(): void {
    if (!this.meterFill || this.meterGaugeWidth <= 0) return;
    const radius = Math.max(this.meterGaugeHeight / 2 - 1, 5);
    const x = this.meterGaugeX + 2;
    const y = this.meterGaugeY - this.meterGaugeHeight / 2 + 2;
    this.meterFill.clear();
    this.meterFill.fillStyle(this.meterFillColor, 1);
    this.meterFill.fillRoundedRect(x, y, Math.max(this.meterDisplayWidth - 4, 4), this.meterGaugeHeight - 4, radius);
    if (this.meterDisplayWidth > 30) {
      this.meterFill.lineStyle(1, Phaser.Display.Color.GetColor(255, 255, 255), 0.6);
      this.meterFill.strokeRoundedRect(x, y, Math.max(this.meterDisplayWidth - 4, 4), this.meterGaugeHeight - 4, radius);
    }
  }

  private updateMeterTarget(): void {
    if (this.meterGaugeWidth <= 0) return;
    const normalized = Phaser.Math.Clamp(this.meterValue / 100, 0, 1);
    this.meterTargetWidth = Math.max(normalized * this.meterGaugeWidth, 4);
    this.meterFillColor = this.getMeterFillColor(normalized);
  }

  private animateMeterFill(): void {
    if (!this.meterFill) return;
    const target = this.meterTargetWidth;
    const display = Phaser.Math.Linear(this.meterDisplayWidth, target, 0.12);
    this.meterDisplayWidth = Math.abs(display - target) < 0.5 ? target : display;
    this.redrawMeterFill();
  }

  private getMeterFillColor(normalized: number): number {
    if (normalized >= 0.6) {
      return THEME.colors.hit.goodHex;
    }
    if (normalized >= 0.3) {
      return THEME.colors.yellowHex;
    }
    return THEME.colors.hit.badHex;
  }

  private changeMeterValue(delta: number): void {
    if (this.eliminated || !this.isPlaying) return;
    this.meterValue = Phaser.Math.Clamp(this.meterValue + delta, 0, 100);
    this.updateMeterTarget();
    if (this.meterValue <= 0) {
      this.eliminated = true;
      this.endGame();
    }
  }

  private applyMeterGain(quality: ScoredQuality): void {
    if (quality === 'bad') {
      this.applyMeterPenalty();
      return;
    }
    const accuracyWeight = quality === 'perfect' ? 2 : quality === 'good' ? 1.3 : 0.75;
    this.consecutiveMisses = 0;
    this.changeMeterValue(accuracyWeight);
  }

  private applyMeterPenalty(): void {
    this.consecutiveMisses = Math.min(this.consecutiveMisses + 1, 5);
    const penalty = 12 + 6 * (this.consecutiveMisses - 1);
    this.changeMeterValue(-penalty);
  }

  private createLane(): void {
    const settings = loadSettings();
    const binding = settings.inputBindings[0]?.type === 'keyboard'
      ? settings.inputBindings[0]
      : { playerId: 0, type: 'keyboard' as const, key: settings.inputKey };

    this.lane = new PlayerLane(this, {
      layout: this.layout,
      playerName: 'Player',
      isSinglePlayer: true,
      inputBinding: binding,
      difficulty: this.difficultyConfig,
      noteMessage: '',
      targetMessage: '',
      borderColor: getLocalSeatColor(0, 1, [0]) ?? THEME.colors.borderHex,
      vfxManager: this.vfxManager,
      audioManager: this.audioManager,
      onLetterDecoded: (letter) => this.handleLetterDecoded(letter),
      onLaneHit: (event) => this.handleLaneHit(event),
      onLanePenalty: (event) => this.handleLanePenalty(event),
    });

    this.lane.inputManager.onInputDown((time) => this.handleInputDown(time));
    this.lane.inputManager.onInputUp((time, duration) => this.handleInputUp(time, duration));

    const mouseInput = new InputManager(this, { playerId: 0, type: 'mouse', mouseButton: 0 });
    mouseInput.onInputDown((time) => this.handleInputDown(time));
    mouseInput.onInputUp((time, duration) => this.handleInputUp(time, duration));
    this.extraInputManagers.push(mouseInput);

    this.setupTargetStream();
  }

  private startGame(): void {
    this.isPlaying = true;
    this.gameStartTime = this.time.now;
    this.rampStartTime = this.time.now;
    this.nextRampAt = this.rampStartTime + this.rampTickMs;
    this.lane.noteTrack.start(this.gameStartTime);
    this.lane.sentTrack.start();
    this.lane.inputManager.enable();
    for (const manager of this.extraInputManagers) {
      manager.enable();
    }
    this.applyRamp(this.time.now);
  }

  private applyRamp(time: number): void {
    if (!this.rampConfig) return;
    if (time < this.nextRampAt) return;
    this.nextRampAt += this.rampTickMs;
    this.setScrollSpeed(this.currentSpeed + this.rampStep);
    this.audioManager.playSfx('challenge_speedup');
    this.pulseSpeedHud();
  }

  private setScrollSpeed(speed: number): void {
    if (!Number.isFinite(speed) || speed <= 0) return;
    this.currentSpeed = speed;
    this.difficultyConfig.scrollSpeed = speed;
    this.lane.noteTrack.setScrollSpeed(speed);
    this.lane.sentTrack.setScrollSpeed(speed);
  }

  private pulseSpeedHud(): void {
    this.speedUpText?.destroy();
    const anchorX = this.layout.trackX + this.layout.trackWidth - 20;
    const anchorY = this.layout.trackY - this.layout.trackHeight / 2 - 28;
    this.speedUpText = this.add.text(anchorX, anchorY, 'SPEED UP', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.small,
      color: THEME.colors.yellow,
    }).setOrigin(1, 0.5);

    this.speedPulse?.stop();
    this.speedUpText.setScale(1);
    this.speedPulse = this.tweens.add({
      targets: this.speedUpText,
      scale: { from: 1.35, to: 1 },
      duration: 420,
      ease: 'Back.easeOut',
    });

    this.tweens.add({
      targets: this.speedUpText,
      alpha: { from: 1, to: 0 },
      y: this.speedUpText.y - 16,
      duration: 1400,
      onComplete: () => {
        this.speedUpText?.destroy();
        this.speedUpText = null;
      },
    });
  }

  private handleInputDown(time: number): void {
    if (!this.isPlaying || this.eliminated) return;

    this.lane.setHitZonePressed(true);

    this.activeToneHolders++;
    if (this.activeToneHolders === 1) {
      this.audioManager.startTone();
    }

    const downResult = this.lane.handleKeyDown(time);
    if (!downResult.note) {
      return;
    }
    if (downResult.timingFeedback === 'EARLY') {
      this.lane.showFeedbackText('EARLY', THEME.colors.yellow);
    } else if (downResult.timingFeedback === 'LATE') {
      this.lane.showFeedbackText('LATE', THEME.colors.hit.ok);
    }
  }

  private handleInputUp(time: number, duration: number): void {
    if (!this.isPlaying || this.eliminated) return;

    this.lane.setHitZonePressed(false);

    this.activeToneHolders = Math.max(0, this.activeToneHolders - 1);
    if (this.activeToneHolders === 0) {
      this.audioManager.stopTone();
    }

    const threshold = getInputThresholdMs(this.difficultyConfig);
    const playerSymbol: MorseSymbol = duration < threshold ? '.' : '-';

    const upResult = this.lane.handleKeyUp(time, duration, playerSymbol);

    if (!upResult.note) {
      return;
    }
  }

  private checkPassedNotes(): void {
    this.lane.checkPassedNotes();
  }

  private handleLaneHit(event: LaneHitEvent): void {
    // Lane handles VFX/audio; we just update the meter
    this.applyMeterGain(event.quality);
  }

  private handleLanePenalty(_event: LanePenaltyEvent): void {
    // Lane handles VFX/audio; we just update the meter
    this.applyMeterPenalty();
  }

  private endGame(): void {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    this.audioManager.stopTone();
    this.activeToneHolders = 0;

    this.lane.stopInput();
    for (const manager of this.extraInputManagers) {
      manager.disable();
    }

    const results = this.lane.getResults();
    const streak = this.lane.getMaxStreak();

    // Track game completion
    trackGameComplete({
      mode: 'daily',
      score: results.score,
      grade: results.grade,
      success: !this.eliminated,
    });

    this.cameras.main.fadeOut(300, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('DailyResultScene', {
        score: results.score,
        streak,
        decodedMessage: this.lane.sentTrack.getFormattedDecodedMessage(),
        intendedMessage: this.message,
        accuracy: results.accuracy,
        grade: results.grade,
        dailyDate: this.dailyDate,
        dailyId: this.dailyId,
        eliminated: this.eliminated,
      });
    });
  }

  private setupTargetStream(): void {
    const normalized = this.message.trim().replace(/\\s+/g, ' ');
    const words = normalized.split(' ').filter(Boolean);
    const chunks: string[] = [];
    this.wordLengths = words.map((word) => word.length);

    // Pass full message to SentTrack (it will parse internally)
    this.lane.sentTrack.setIntendedMessage(this.message);

    this.currentWordIndex = 0;
    this.currentWordBuffer = '';
    this.pendingWords = [];
    for (const word of words) {
      let idx = 0;
      while (idx < word.length) {
        chunks.push(word.slice(idx, idx + 5));
        idx += 5;
      }
    }
    this.targetChunks = chunks;
    this.nextChunkIndex = 0;
    this.ensureTargetBuffer(true);
  }

  private ensureTargetBuffer(force: boolean = false): void {
    if (!this.lane) return;
    const remaining = this.lane.noteTrack.getStaticLetterCount();
    if (!force && remaining > 3) return;
    this.appendNextChunk();
  }

  private appendNextChunk(): void {
    if (!this.lane) return;
    if (this.nextChunkIndex >= this.targetChunks.length) return;
    const chunk = this.targetChunks[this.nextChunkIndex];
    this.nextChunkIndex += 1;
    let addedNotes = 0;
    for (const char of chunk) {
      addedNotes += this.lane.noteTrack.appendLetterFromRightEdge(char);
    }
    if (addedNotes > 0) {
      this.lane.addTotalNotes(addedNotes);
    }
  }

  private setupTranscriptArea(): void {
    const areaX = this.layout.trackX + 40;
    const areaWidth = this.layout.trackWidth * 0.25;
    const fontSizePx = Number.parseInt(THEME.sizes.bodyLarge, 10) || 18;
    const lineHeight = fontSizePx + 8;
    const areaY = this.layout.trackY - this.layout.trackHeight / 2 - 20;

    this.transcriptArea = { x: areaX, y: areaY, width: areaWidth, lineHeight };
    this.transcriptLines = [];
    this.transcriptTexts = [];

    for (let i = 0; i < this.transcriptMaxLines; i++) {
      // Index 0 = oldest = highest position; last index = newest = closest to lane
      const line = this.add.text(areaX, areaY - (this.transcriptMaxLines - 1 - i) * lineHeight, '', {
        fontFamily: THEME.fonts.mono,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.yellow,
        stroke: THEME.stroke.color,
        strokeThickness: THEME.stroke.thicknessThin,
      }).setOrigin(0, 0);
      this.transcriptTexts.push(line);
    }

    this.transcriptMeasure = this.add.text(0, 0, '', {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.bodyLarge,
    }).setVisible(false);
  }

  private handleLetterDecoded(letter: string): void {
    if (this.currentWordIndex >= this.wordLengths.length) return;
    this.currentWordBuffer += letter;
    while (this.currentWordIndex < this.wordLengths.length) {
      const needed = this.wordLengths[this.currentWordIndex];
      if (this.currentWordBuffer.length < needed) return;
      const word = this.currentWordBuffer.slice(0, needed);
      this.currentWordBuffer = this.currentWordBuffer.slice(needed);
      this.pendingWords.push(word);
      this.currentWordIndex += 1;
    }
  }

  private processPendingWords(): void {
    if (!this.pendingWords.length || !this.lane) return;
    const nextWord = this.pendingWords[0];
    if (this.lane.sentTrack.getStoppedLetterCount() < nextWord.length) return;
    this.pendingWords.shift();
    this.lane.sentTrack.removeStoppedLetters(nextWord.length);
    this.pushTranscriptWord(nextWord);
  }

  private pushTranscriptWord(word: string): void {
    if (!this.transcriptArea || !this.transcriptMeasure) return;
    const area = this.transcriptArea;

    const lastIndex = Math.max(0, this.transcriptLines.length - 1);
    let lineIndex = this.transcriptLines.length ? lastIndex : 0;
    let currentLine = this.transcriptLines[lineIndex] ?? '';
    const prefix = currentLine.length ? `${currentLine} ` : '';
    const candidate = `${prefix}${word}`;
    const candidateWidth = this.measureTranscriptWidth(candidate);

    if (candidateWidth > area.width) {
      lineIndex = this.transcriptLines.length;
      currentLine = '';
      // Scroll existing lines up
      for (const textObj of this.transcriptTexts) {
        this.tweens.add({
          targets: textObj,
          y: textObj.y - area.lineHeight,
          duration: 200,
          ease: 'Quad.easeOut',
        });
      }
    }

    if (lineIndex >= this.transcriptLines.length) {
      this.transcriptLines.push(word);
    } else {
      this.transcriptLines[lineIndex] = `${currentLine.length ? `${currentLine} ` : ''}${word}`;
    }

    if (this.transcriptLines.length > this.transcriptMaxLines) {
      this.transcriptLines.shift();
      lineIndex = Math.max(0, lineIndex - 1);
    }

    const startX = this.layout.accumulatedStartX;
    const startY = this.layout.trackY - this.layout.trackHeight / 2 - 16;
    const lineText = this.transcriptLines[lineIndex] ?? '';
    const beforeText = lineText.endsWith(word)
      ? lineText.slice(0, Math.max(0, lineText.length - word.length)).trimEnd()
      : '';
    const offsetX = this.measureTranscriptWidth(beforeText ? `${beforeText} ` : '');
    const targetX = area.x + offsetX;
    const targetY = area.y - (this.transcriptMaxLines - 1 - lineIndex) * area.lineHeight;

    const flying = this.add.text(startX, startY, word, {
      fontFamily: THEME.fonts.mono,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.yellow,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0, 0);

    this.tweens.add({
      targets: flying,
      x: targetX,
      y: targetY,
      duration: 650,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.refreshTranscriptText();
        flying.destroy();
      },
    });
  }

  private refreshTranscriptText(): void {
    for (let i = 0; i < this.transcriptTexts.length; i++) {
      const line = this.transcriptLines[i] ?? '';
      this.transcriptTexts[i].setText(line);
      this.transcriptTexts[i].setVisible(Boolean(line));
    }
  }

  private measureTranscriptWidth(text: string): number {
    if (!this.transcriptMeasure) return 0;
    this.transcriptMeasure.setText(text);
    return this.transcriptMeasure.width;
  }
}
